import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Send, X, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const CAN_CREATE = ['group-admin', 'site-admin', 'admin'];

// Request + create-a-group-site actions. Shown on the public Groups page and the
// member dashboard. "Request a group site" is open to anyone; "Create a new site"
// only appears for group administrators (and above).
export default function GroupSiteActions({ showRequest = true, showCreate = true, compact = false, onCreated }) {
  const { user, requestSubgroup, createSubgroupSelf, checkSubgroupName } = useAuth();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null); // 'request' | 'create'
  const [busy, setBusy] = useState(false);
  const [req, setReq] = useState({ group_name: '', contact_name: '', email: '', phone: '', message: '' });
  const [form, setForm] = useState({ name: '', tagline: '', intro: '', join_fee: '' });

  const canCreate = !!user && CAN_CREATE.includes(user.memberClass);

  const submitRequest = async () => {
    if (!req.group_name.trim() || !req.email.trim()) { toast.error('Please enter the group name and your email.'); return; }
    setBusy(true);
    try { await requestSubgroup(req); toast.success('Request sent — an administrator will be in touch.'); setModal(null); setReq({ group_name: '', contact_name: '', email: '', phone: '', message: '' }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send your request.'); }
    finally { setBusy(false); }
  };

  const submitCreate = async () => {
    if (!form.name.trim()) { toast.error('Please enter a group name.'); return; }
    setBusy(true);
    try {
      const chk = await checkSubgroupName(form.name.trim());
      if (chk.exists && !window.confirm(`A group named "${form.name.trim()}" already exists. Create another with the same name anyway?`)) {
        setBusy(false); return;
      }
      const r = await createSubgroupSelf({
        name: form.name, tagline: form.tagline, intro: form.intro,
        join_fee_cents: Math.max(0, Math.round(parseFloat(form.join_fee || '0') * 100) || 0),
      });
      setModal(null); setForm({ name: '', tagline: '', intro: '', join_fee: '' });
      if (r.pending) toast.success('Group created — it is awaiting administrator approval before it goes live.');
      else { toast.success('Group created and live.'); navigate(`/g/${r.slug}`); }
      if (onCreated) onCreated(r);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create the group.'); }
    finally { setBusy(false); }
  };

  const field = (label, val, on, props = {}) => (
    <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>{label}</label>
      <input value={val} onChange={(e) => on(e.target.value)} className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} {...props} /></div>
  );

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {showCreate && canCreate && (
          <button onClick={() => setModal('create')} data-testid="create-group-btn" title="Create a new group site"
            className={compact ? 'inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-sm font-title' : 'inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-title'} style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
            <PlusCircle size={16} /> {compact ? 'New site' : 'Create a new site'}
          </button>
        )}
        {showRequest && (
          <button onClick={() => setModal('request')} data-testid="request-group-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-title border" style={{ borderColor: 'var(--maroon)', color: 'var(--maroon)', background: 'transparent' }}>
            <Send size={15} /> Request a group site
          </button>
        )}
      </div>

      {modal === 'request' && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="request-group-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Request a group site</h3>
              <button onClick={() => setModal(null)} data-testid="request-group-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-2.5">
              <p className="text-sm" style={{ color: 'var(--olive)' }}>Tell us about the group you'd like. An administrator will review your request and set you up as its group administrator.</p>
              {field('Group name *', req.group_name, (v) => setReq({ ...req, group_name: v }), { 'data-testid': 'request-group-name' })}
              {field('Your name', req.contact_name, (v) => setReq({ ...req, contact_name: v }), { 'data-testid': 'request-contact-name' })}
              {field('Your email *', req.email, (v) => setReq({ ...req, email: v }), { type: 'email', 'data-testid': 'request-email' })}
              {field('Phone', req.phone, (v) => setReq({ ...req, phone: v }), { 'data-testid': 'request-phone' })}
              <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>What's it for?</label>
                <textarea value={req.message} onChange={(e) => setReq({ ...req, message: e.target.value })} rows={3} data-testid="request-message" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
              <button onClick={submitRequest} disabled={busy} data-testid="request-group-submit" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{busy && <Loader2 size={14} className="animate-spin" />} Send request</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'create' && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="create-group-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Create a new group site</h3>
              <button onClick={() => setModal(null)} data-testid="create-group-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-2.5">
              <p className="text-sm inline-flex items-center gap-1.5" style={{ color: 'var(--olive)' }}><Clock size={14} /> New sites go live once a site administrator approves them.</p>
              {field('Group name *', form.name, (v) => setForm({ ...form, name: v }), { 'data-testid': 'create-group-name' })}
              {field('Tagline', form.tagline, (v) => setForm({ ...form, tagline: v }), { 'data-testid': 'create-group-tagline' })}
              <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Short intro</label>
                <textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} rows={3} data-testid="create-group-intro" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
              {field('Annual join fee (AUD, 0 = free)', form.join_fee, (v) => setForm({ ...form, join_fee: v }), { type: 'number', min: '0', step: '0.01', 'data-testid': 'create-group-fee' })}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
              <button onClick={submitCreate} disabled={busy} data-testid="create-group-submit" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{busy && <Loader2 size={14} className="animate-spin" />} Create site</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
